import { DEFAULT_REGION_BANK_HEADER_VALUE } from '@/constants'
import type {
  AvailableOptionInterface,
  CartProductInterface,
  OrderPreviewProductModel,
  SubOrderRequirementInterface,
} from '@/types'
import api from '@/utils/api/api'

export interface OrderPreviewProduct {
  key: {
    supplierId: string
    productCode: number
    storeCode: number
  }
  quantity: number
  route?: string[]
}

export interface OrderPreviewRequest {
  client: string | null
  products: OrderPreviewProduct[]
}

export interface OrderPreviewRequiredInput {
  type: 'CONTRACT' | 'CONSTRUCTION_SITE' | 'DESTINATION_STORE'
  options: AvailableOptionInterface[]
}

export interface OrderPreviewResponse {
  scenario: string
  summary: {
    totalPrice: number
    currencyCode: number
  }
  mainOrderRequirement: {
    products: OrderPreviewProductModel[]
    requiredInputs: OrderPreviewRequiredInput[]
  } | null
  subOrderRequirements: Array<
    Omit<SubOrderRequirementInterface, 'products'> & {
      products: OrderPreviewProductModel[]
    }
  >
}

export class OrderPreviewService {
  async getOrderPreview(
    clientId: string | null,
    products: CartProductInterface[]
  ): Promise<OrderPreviewResponse> {
    const requestBody: OrderPreviewRequest = {
      client: clientId,
      products: products.map((product) => {
        const stockItem = product.suppliersStock?.[0]?.stock?.[0]

        const store = stockItem?.storeShortModel
        const supplierId = store?.supplierId ?? ''

        const quantity =
          stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart ?? 0

        return {
          key: {
            supplierId: supplierId,
            productCode: product.nomenclatureDto?.code,
            storeCode: store?.code || 0,
          },
          quantity: quantity,
          route: stockItem?.route,
        }
      }),
    }

    const response = await api.post<
      OrderPreviewResponse | { preview: OrderPreviewResponse }
    >('/orders/preview', requestBody, {
      headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
    })

    const data: OrderPreviewResponse | { preview: OrderPreviewResponse } =
      response.data
    if ('preview' in data && data.preview) return data.preview
    return data as OrderPreviewResponse
  }
}

export const orderPreviewService = new OrderPreviewService()
